// Delegated enhancement survives Shopify section replacement without duplicate bindings.
if (!window.mewoDisclosureKeysBound) {
  window.mewoDisclosureKeysBound = true;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.mewo details[open]');
    if (!disclosure) return;
    disclosure.open = false;
    disclosure.querySelector('summary')?.focus();
    event.preventDefault();
  });
}

// Store product handles only. Uploads and personalization remain exclusively in the native form.
// Avante owns cart mutations; refresh this shell's badge after its successful update event.
if (!window.mewoCartCountBound && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  window.mewoCartCountBound = true;
  let revision = 0;
  subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
    const current = ++revision;
    const link = document.querySelector('.mewo-tools a[href] .mewo-cart-count')?.closest('a');
    if (!link) return;
    try {
      const url = new URL(link.href, location.origin);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (current !== revision || !Number.isInteger(cart.item_count) || cart.item_count < 0) return;
      document.querySelectorAll('.mewo-cart-count').forEach(node => { node.textContent = String(cart.item_count); node.hidden = cart.item_count === 0; });
    } catch { /* Keep the last server-rendered count when the read fails. */ }
  });
}

if (!window.mewoSavedDesignsBound) {
  window.mewoSavedDesignsBound = true;
  const key = 'mewo.saved-designs.v1';
  const valid = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const read = () => {
    try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? [...new Set(value.filter(valid))].slice(0,50) : []; }
    catch { return []; }
  };
  const sync = () => document.querySelectorAll('[data-mewo-save]').forEach(button => {
    const saved = read().includes(button.dataset.mewoSave);
    button.setAttribute('aria-pressed', String(saved));
    const label = button.querySelector('.mewo-save-text');
    if (label) label.textContent = saved ? 'Saved design' : 'Save design';
  });
  const status = message => {
    let node = document.getElementById('MewoSavedStatus');
    if (!node) { node = document.createElement('p'); node.id = 'MewoSavedStatus'; node.className = 'visually-hidden'; node.setAttribute('role','status'); document.body.append(node); }
    node.textContent = message;
  };
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-mewo-save]');
    if (!button || !valid(button.dataset.mewoSave)) return;
    const handle = button.dataset.mewoSave, saved = read(), exists = saved.includes(handle);
    if (!exists && saved.length >= 50) { status('Your shortlist is full. Remove a design before saving another.'); return; }
    try { localStorage.setItem(key, JSON.stringify(exists ? saved.filter(x => x !== handle) : [...saved,handle])); }
    catch { status('This browser could not save your design. Check browser storage settings.'); return; }
    sync(); status(exists ? 'Design removed from your shortlist.' : 'Design saved in this browser.');
    if (document.querySelector('[data-mewo-saved-page]')) renderSaved();
  });
  let generation = 0;
  async function renderSaved() {
    const page = document.querySelector('[data-mewo-saved-page]');
    if (!page) return;
    const current = ++generation, grid = page.querySelector('[data-saved-grid]'), message = page.querySelector('[data-saved-status]'), handles = read();
    grid.replaceChildren(); message.textContent = handles.length ? 'Loading your saved designs…' : 'Your shortlist is empty. Tap a heart on a design to save it here.';
    const results = await Promise.allSettled(handles.map(async handle => {
      const response = await fetch(`${page.dataset.root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}.js`, { credentials:'same-origin', headers:{Accept:'application/json'} });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      if (!product.title || product.handle !== handle) throw new Error('Invalid product');
      return product;
    }));
    if (current !== generation || !page.isConnected) return;
    let failed = 0;
    results.forEach((result,i) => {
      const card = document.createElement('article'); card.className = 'mewo-card';
      const link = document.createElement('a'); link.className = 'mewo-product-card';
      const heading = document.createElement('h3');
      if (result.status === 'fulfilled') {
        const product = result.value;
        link.href = `${page.dataset.root.replace(/\/?$/, '/')}products/${encodeURIComponent(product.handle)}`;
        if (product.featured_image) {
          const url = new URL(product.featured_image, location.origin);
          if (url.protocol === 'https:') { const media = document.createElement('div'); media.className = 'mewo-card-media'; const img = document.createElement('img'); url.searchParams.set('width','600'); img.src=url.href; img.alt=product.title; img.width=600; img.height=600; img.loading='lazy'; media.append(img); link.append(media); }
        }
        heading.textContent = product.title; link.append(heading);
        const price = document.createElement('p');
        price.textContent = `${product.price_varies ? 'From ' : ''}${new Intl.NumberFormat(page.dataset.locale || 'en', { style:'currency',currency:page.dataset.currency || 'USD' }).format(product.price/100)}`;
        link.append(price); card.append(link);
      } else { failed++; heading.textContent = 'This saved design is currently unavailable.'; card.append(heading); }
      const remove = document.createElement('button'); remove.type='button'; remove.className='mewo-save'; remove.dataset.mewoSave=handles[i]; remove.setAttribute('aria-pressed','true'); remove.textContent='Remove'; remove.style.position='static'; remove.style.width='auto'; card.append(remove); grid.append(card);
    });
    message.textContent = failed ? `${failed} saved design${failed === 1 ? ' is' : 's are'} unavailable. You can remove ${failed === 1 ? 'it' : 'them'} from your shortlist.` : (handles.length ? `${handles.length} saved design${handles.length === 1 ? '' : 's'}.` : message.textContent);
  }
  document.addEventListener('shopify:section:load', () => { sync(); renderSaved(); });
  window.addEventListener('storage', event => { if (event.key === key) { sync(); renderSaved(); } });
  sync(); renderSaved();
}

// Native navigation disclosures: close on outside click or focus leaving the header.
// Copy the support email only when explicitly requested by the visitor.
if (!window.mewoEmailCopyBound) {
 window.mewoEmailCopyBound = true;
 let copyStatusTimer;
 document.addEventListener('click', async event => {
  const button=event.target.closest('[data-mewo-copy-email]');
  if(!button || location.pathname.includes('/policies/')) return;
  const email=button.dataset.mewoCopyEmail;
  let copied=false;
  try { await navigator.clipboard.writeText(email); copied=true; } catch {
   const input=document.createElement('textarea');input.value=email;input.setAttribute('readonly','');input.style.cssText='position:fixed;left:-9999px';document.body.append(input);input.select();
   try { copied=document.execCommand('copy'); } catch {} input.remove();button.focus();
  }
  let status=document.querySelector('.mewo-email-status');
  if(!status){status=document.createElement('div');status.className='mewo-email-status';status.setAttribute('role','status');document.body.append(status);}
  status.textContent=copied?'Email address copied.':'Please copy: '+email;
  clearTimeout(copyStatusTimer);copyStatusTimer=setTimeout(()=>{status.textContent='';},5000);
 });
}
if (!window.mewoNavigationBound) {
 window.mewoNavigationBound = true;
 document.addEventListener('click', event => { document.querySelectorAll('.mewo-header details[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; }); });
 // Keep a nested menu stable until the focused sibling link is activated.
 document.addEventListener('focusin', event => { document.querySelectorAll('.mewo-header details[open]').forEach(menu => { const parent=menu.parentElement.closest('.mewo-header details[open]'); if (!menu.contains(event.target) && !parent?.contains(event.target)) menu.open = false; }); });
 document.addEventListener('change', event => { if (event.target.matches('[data-mewo-sort] select')) event.target.form.requestSubmit(); });
}
